
import SkeletonType2 from "@/app/components/ui/Skeletons/Type2";
import { useSendVCLinkToUserAPI } from "@/app/hooks/api_hooks/bookings/useSendVCLinkToUserAPI";
import { useVCSlotBookingScriptDetailsAPI } from "@/app/hooks/api_hooks/bookings/useVCSlotBookingScriptDetailsAPI";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import WhatsappTemplatePreviewModal from "@/app/components/ui/WhatsappTemplatePreviewModal";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

const SendVCLink = ({ setOpen, open, userId, petId, petName, bookingId }) => {
  const { execute: fetchUserdetails, userDetails } = useVCSlotBookingScriptDetailsAPI();
  const { execute: sendVCLink } = useSendVCLinkToUserAPI();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("CUSTOMER_PROFILE", "id");
  const { routeState } = useRouteInfo(routeId);
  const { customerDetails } = routeState?.state as ICustomerProfileState;
  const { execute: logAPI } = useClickLogAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  const [loader, setLoader] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  useEffect(() => {
    if (open && userId) {
      setLoader(true);
      fetchUserdetails(userId).then(({ status }) => {
        setLoader(false);
      });
    }
  }, [open, userId, fetchUserdetails]);

  const handleSendVCLink = (mobile: string) => {
    setIsSending(true);
    sendVCLink(userId, petId, mobile, bookingId).then(({ status }) => {
      setIsSending(false);
      if (status) {
        const logBody: IClickLogBody = {
          event_type: "WHATSAPP_VC_LINK_SENT",
          client_id: userId ? userId : null,
          pet_id: petId ? petId : null,
          mobile_number: mobile,
          vms_user_id: userLogInfo,
          scr_id: "SEND_VC_LINK_MODAL",
          timestamp: Date.now(),
        };
        logAPI(logBody);
        setOpen(false);
      }
    });
  };

  if (loader) {
    return (
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="z-50 fixed inset-0 overflow-y-auto"
          onClose={() => setOpen(false)}
        >
          <div className="sm:block flex justify-center items-end px-4 sm:p-0 pt-4 pb-20 min-h-screen text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="sm:inline-block hidden sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white shadow-xl sm:my-8 p-[16px] rounded-[12px] max-w-[516px] w-full text-left transform transition-all sm:align-middle">
                <SkeletonType2 />
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    );
  }

  return (
    <WhatsappTemplatePreviewModal
      open={open}
      onClose={() => setOpen(false)}
      title="Send Video Consultation Link"
      initialMobile={customerDetails?.mobile ?? ""}
      script={userDetails?.script ?? ""}
      isSending={isSending}
      onSend={handleSendVCLink}
      onCancel={() => setOpen(false)}
    />
  );
};

export default SendVCLink;
